import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  DialogRef,
  DialogService,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import { Enums, Hunt, HuntCoverage } from '@cyborg-monorepo/hunter/common';
import { ListItemModel } from '@progress/kendo-angular-buttons';
import { GridComponent } from '@progress/kendo-angular-grid';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';
import { asBlob } from 'html-docx-js-typescript';
import * as sunburstChartJson from '../../hunt/overview/dashboard/charts/sunburst-chart.json'; // Import JSON file
import * as threatPieChartJson from '../../hunt/overview/dashboard/charts/threat-pie-chart.json'; // Import JSON file
import { saveAs } from 'file-saver';
import _ from 'lodash-es';

import {
  TrackingEventEnum,
  TrackingService,
} from '../../../tracking/tracking.service';
import { HuntService } from '../../shared/services/hunt.service';
import { ArchiveHuntDialogComponent } from '../../hunt/overview/archive-hunt-dialog/archive-hunt-dialog.component';
import { EditHuntDialogComponent } from '../../hunt/hunt/edit-hunt-dialog/edit-hunt-dialog.component';
import * as echarts from 'echarts/core';

import {
  LineChart,
  SunburstChart,
  PieChart,
  HeatmapChart,
} from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  GridComponent as eGridComponent,
  LegendComponent,
  GraphicComponent,
  VisualMapComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { addNewLinesBetweenWords } from '../../../helpers/utils';
import {
  ChartUtils,
  SunburstData,
} from '../../hunt/overview/dashboard/charts/chart-utils';
import { PageMargin } from '@progress/kendo-drawing/dist/npm/pdf';
import { ExportOptionsDialogComponent } from '../../hunt/overview/dashboard/export-options-dialog/export-options-dialog.component';
import { HuntMetricsService } from '../../shared/services/hunt-metrics.service';
import { HuntContextService } from '../../../pages/hunt/services/hunt-context.service';
import { BadgeComponent, BreadcrumbItem, BreadcrumbListComponent, ButtonComponent, ButtonMenuComponent, DropdownMenuItemComponent, ErrorComponent, TagComponent, TypographyComponent } from '@hyperkit';
import { CommonModule } from '@angular/common';
import { ReportSectionComponent } from '../report-section/report-section.component';
import { ReportTextBlockComponent } from './report-text-block/report-text-block.component';

echarts.use([
  TitleComponent,
  TooltipComponent,
  eGridComponent,
  LegendComponent,
  VisualMapComponent,
  LineChart,
  CanvasRenderer,
  GraphicComponent,
  SunburstChart,
  PieChart,
  HeatmapChart,
]);

export enum ReportChartExports {
  EXECUTIVE_SUMMARY = 'Executive Summary',
  ABSTRACT = 'Abstract',
  HYPOTHESIS = 'Hypothesis',
  TECHNICAL_SUMMARY = 'Technical Summary',
  CONCLUSION = 'Conclusion',
  COVERAGE = 'Coverage',
}

export interface IData {
  actionName: string;
  icon: string;
  disabled?: boolean;
}
@Component({
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonMenuComponent, ReportTextBlockComponent, ReportSectionComponent, BreadcrumbListComponent, DropdownMenuItemComponent,TypographyComponent, ButtonComponent, ErrorComponent, BadgeComponent, TagComponent],
  selector: 'hunt-final-report',
  templateUrl: './hunt-final-report.component.html',
  styleUrls: ['hunt-final-report.component.scss'],
})
export class HuntFinalReportComponent implements OnInit {
  readonly huntContext = inject(HuntContextService);
  readonly huntService = inject(HuntService); 
  readonly dialogService = inject(DialogService); 
  readonly snackBarService = inject(MatSnackBar); 
  readonly huntMetricsService = inject(HuntMetricsService); 
  readonly trackingService = inject(TrackingService); 
  readonly router = inject(Router)
  readonly route = inject(ActivatedRoute);
  readonly cdr = inject(ChangeDetectorRef);
  public breadcrumbs = signal<BreadcrumbItem[]>([]);  // Initialize with empty array


  constructor() {
    const route = inject(ActivatedRoute);
    const resolvedHunt = route.snapshot.data['hunt'] as Hunt | null;
  
      if (resolvedHunt) {
        this.huntContext.state.updateHunt(resolvedHunt);   
        this.huntContext.editor.load(resolvedHunt);       
      }

      effect(() => {
        this.breadcrumbs.set([
          { text: 'Hunt', selected: false, link: '/hunt/hunts' },
          { text: this.huntContext.state.name(), selected: true },
        ] as BreadcrumbItem[]); 
        });
  }

  @Input() public hunt: Hunt;
  @ViewChild('pdf') public pdf: PDFExportComponent;
  @ViewChild('reportHtml', { static: false }) public reportHtml!: ElementRef;

  public huntActorCoverage: HuntCoverage[];
  public huntMITRECoverage: HuntCoverage[];
  public huntThreatCoverage: HuntCoverage[];
  public error: string = '';

  // Coverage
  public actorSunburst: echarts.ECharts;
  public actorSunburstOptions = sunburstChartJson;
  public mitreSunburst: echarts.ECharts;
  public mitreSunburstOptions = sunburstChartJson;
  public threatPie: echarts.ECharts;
  public threatPieOptions = threatPieChartJson;
  public imageUrl: string;
  public chartColors = ChartUtils.sunburstColors;
  public hide: boolean = false;
  public exportOptions: ReportChartExports[] = [];

  public selectedSection: 'overview' | 'hunt-package' | 'conclusion' | 'coverage' = 'overview';
  // public sections: Section[] = [];
  public selectedSectionId: string = '';
  public huntPackages = this.huntContext.query.huntPackages();

  readonly sections = computed(() => {
    console.log('Recalculating sections');

    return [
      {
        id: 'overview',
        title: 'Overview',
      },
      {
        id: 'technical-summary',
        title: 'Technical Summary',
        children: [
          ...this.huntContext.query.huntPackages().map((huntPackage) => ({
            id: huntPackage.UUID,
            title: huntPackage.title,
          }))
        ]
      },
      {
        id: 'conclusion',
        title: 'Conclusion',
      },
      {
        id: 'coverage',
        title: 'Coverage',
      }
    ];
  });

  public exportData: ListItemModel[] = [
    {
      text: 'PDF',
      icon: 'pdf',
      click: async (): Promise<void> => {
        const dialogRef: DialogRef = this.dialogService.open(
          ExportOptionsDialogComponent,
          { data: this.exportOptions },
        );

        dialogRef.afterClosed().subscribe((exportOptions) => {
          if (exportOptions) {
            this.removeExtraReportSections(exportOptions as string[]);
            if (
              (exportOptions as string[]).includes(ReportChartExports.COVERAGE)
            ) {
              const elements = document.querySelectorAll('.new-page');
              elements.forEach((element) => {
                element.classList.add('page-break');
              });
            } else {
              const elements = document.querySelectorAll('.page-break');
              elements.forEach((element) => {
                element.classList.remove('page-break');
              });
            }
            this.pdf.saveAs(
              this.hunt.name.split(' ').join('-') +
                '-' +
                this.reportDate +
                '.pdf',
            );
            setTimeout(() => {
              this.resetReportSections();
            }, 2000);
          }
        });
        this.trackingService.trackEvent(TrackingEventEnum.HUNT_REPORT_EXPORTED);
      },
    },
    {
      text: 'Word',
      icon: 'word',
      click: async (): Promise<void> => {
        const dialogRef: DialogRef = this.dialogService.open(
          ExportOptionsDialogComponent,
          { data: this.exportOptions },
        );

        dialogRef.afterClosed().subscribe((exportOptions) => {
          if (exportOptions) {
            const reportHtml = this.removeExtraReportSections(
              exportOptions as string[],
            );

            asBlob(this.generateReportFromHTML(reportHtml)).then((data) => {
              saveAs(
                data,
                this.hunt.name.split(' ').join('-') +
                  '-' +
                  this.reportDate +
                  '.docx',
              );
              setTimeout(() => {
                this.resetReportSections();
              }, 2000);
            });
            this.trackingService.trackEvent(
              TrackingEventEnum.HUNT_REPORT_EXPORTED,
            );
          }
        });
      },
    },
  ];

  public handleMenuAction(event: Event): void {
    console.log('handle menu action');
  }

  public priority = Enums.PriorityEnum;
  public reportDate = new Date().toLocaleDateString();
  public status = Enums.StatusEnum;
  public outcome = Enums.OutcomeEnum;
  public margins: PageMargin = {
    top: '1in',
    bottom: '1in',
    left: '1in',
    right: '1in',
  };

  public outcomeMap = new Map<string, string>([
    [Enums.OutcomeEnum.DETECTION, 'Suggested Detection'],
    [Enums.OutcomeEnum.NO_FINDINGS, 'Hunt Methodology'],
    [Enums.OutcomeEnum.ESCALATED, 'Escalation Details'],
    [
      Enums.OutcomeEnum.VISIBILITY_GAP,
      'Hunt Limitations/Visibility Improvements',
    ],
  ]);

  public outcomeDependentText: string = '';

  public ngOnInit(): void {

    const urlPath = this.router.url;
    this.selectedSectionId = urlPath.split('/').pop() || '';
    
    
    // // Add possible report export options based on report contents
    // if (this.validateReportString(this.hunt?.executiveSummary))
    //   this.exportOptions.push(ReportChartExports.EXECUTIVE_SUMMARY);
    // if (this.validateReportString(this.hunt?.abstract))
    //   this.exportOptions.push(ReportChartExports.ABSTRACT);
    // if (this.validateReportString(this.hunt.hypothesis))
    //   this.exportOptions.push(ReportChartExports.HYPOTHESIS);
    // this.exportOptions.push(ReportChartExports.TECHNICAL_SUMMARY);
    // if (this.validateReportString(this.hunt.conclusion))
    //   this.exportOptions.push(ReportChartExports.CONCLUSION);
    // this.exportOptions.push(ReportChartExports.COVERAGE);

    // // Initialize ECharts instances
    // this.mitreSunburst = echarts.init(
    //   document.getElementById('top-mitre-container'),
    // );
    // this.actorSunburst = echarts.init(
    //   document.getElementById('top-actors-container'),
    // );

    // this.threatPie = echarts.init(
    //   document.getElementById('top-threats-container'),
    // );

    // window.addEventListener('resize', () => {
    //   this.actorSunburst.resize();
    //   this.mitreSunburst.resize();
    // });
    // this.outcomeDependentText = this.outcomeMap.get(this.hunt.outcome);

    // this.huntMetricsService
    //   .getHuntThreatCoverageById(this.hunt.id)
    //   .subscribe((results) => {
    //     this.huntThreatCoverage = results.results;

    //     const threatChartData = results.results.map(
    //       ({ name, huntPackageCount }, index) => ({
    //         value: Number(huntPackageCount),
    //         name: addNewLinesBetweenWords(name),
    //         itemStyle: { color: this.chartColors[index] },
    //       }),
    //     );
    //     this.threatPieOptions.series[0].data = threatChartData;
    //     this.threatPieOptions.series[0].name = 'Top 10 Threats';
    //     this.threatPie.setOption(this.threatPieOptions);
    //   });

    // this.huntMetricsService
    //   .getHuntMITRECoverageById(this.hunt.id)
    //   .subscribe((results) => {
    //     this.huntMITRECoverage = results.results;
    //     const sunburstResults: SunburstData[] = this.createCoverageSunburst(
    //       results.results,
    //     );
    //     this.mitreSunburstOptions.series.data = sunburstResults;
    //     this.mitreSunburst.setOption(this.actorSunburstOptions);
    //   });

    // this.huntMetricsService
    //   .getHuntActorCoverageById(this.hunt.id)
    //   .subscribe((results) => {
    //     this.huntActorCoverage = results.results;
    //     const sunburstResults: SunburstData[] = this.createCoverageSunburst(
    //       results.results,
    //     );
    //     this.actorSunburstOptions.series.data = sunburstResults;
    //     this.actorSunburst.setOption(this.actorSunburstOptions);

    //     const canvas = this.actorSunburst
    //       .getDom()
    //       .getElementsByTagName('canvas')[0];
    //     this.imageUrl = canvas.toDataURL();
    //   });
  }

  public reopenHunt(): void {
    this.huntContext.editor.updateStatus(Enums.StatusEnum.IN_PROGRESS);
    this.huntContext.editor.updateEditing(false);
    this.huntContext.editor.submit();
    this.router.navigate(['hunt-redesign', this.huntContext.state.id(), 'workbench']);


  }

  public archiveHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      ArchiveHuntDialogComponent,
      { data: this.hunt },
    );

    dialogRef.afterClosed().subscribe((hunt: Hunt) => {
      if (hunt) {
        this.router.navigate(['/hunt/hunts']);
        this.snackBarService.open('Archived ' + hunt.name, null, {
          duration: 5 * 1000,
        });
      }
    });
  }

  public editHunt(): void {
    const dialogRef: DialogRef = this.dialogService.open(
      EditHuntDialogComponent,
      { data: this.hunt },
    );

    dialogRef.afterClosed().subscribe((updatedHunt: Hunt) => {
      if (updatedHunt) this.hunt = updatedHunt;
    });
  }

  public editReport(): void {
    this.huntService.closeHunt(this.hunt.id).subscribe((result) => {
      this.hunt = result;
    });
  }

  public scrollTo(target: string): void {
    document.getElementById(target).scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  }

  /**
   * Checks if the provided string contains content and should be shown in the hunt report.
   *
   * A report string is considered valid if it is not null, undefined, or an empty string,
   * and it is not equal to the string '<p></p>'.
   *
   * @param kendoString - The report string to validate.
   * @returns `true` if the string is valid, otherwise `false`.
   */
  public validateReportString(reportString: string): boolean {
    return reportString && reportString != '<p></p>' && reportString != '';
  }

  public exportToPDF(grid: GridComponent): void {
    grid.saveAsPDF();
  }

  public generateReportFromHTML(targetHtml: string): string {
    let html: string =
      '<html><head><style> .branding { text-align: center; } h1 { text-transform: uppercase } html { font-family: "Roboto", "Helvetica"; } </style></head><body><p class="branding">Report generated by Intel 471</p>';
    html += targetHtml + '</body></html>';

    return html;
  }

  public removeExtraReportSections(exportOptions: string[]): string {
    const filteredValues = this.exportOptions.filter(
      (value) => !(exportOptions as string[]).includes(value),
    );

    filteredValues.forEach((option) => {
      document.getElementById(_.kebabCase(option)).classList.add('no-export');
    });

    const originalElement = this.reportHtml.nativeElement as HTMLElement;
    const clonedElement = originalElement.cloneNode(true) as HTMLElement;

    const sections = clonedElement.querySelectorAll('.no-export');
    sections.forEach((section) => section.remove());

    return clonedElement.innerHTML;
  }

  public resetReportSections(): void {
    const elements = document.querySelectorAll('.no-export');

    elements.forEach((element) => {
      element.classList.remove('no-export');
    });
  }

  public createCoverageSunburst(results: HuntCoverage[]): SunburstData[] {
    const sunburstData: SunburstData[] = [];
    const country_set: Set<string> = new Set();
    let count = 0;
    for (const result of results) {
      if (!country_set.has(result.category)) {
        const children: SunburstData[] = [];
        count++;
        children.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntPackageCount,
        });
        sunburstData.push({
          itemStyle: { color: `${this.chartColors[count]}` },
          name: addNewLinesBetweenWords(result.category),
          children,
        });
        country_set.add(result.category);
      } else {
        const targetCategory = sunburstData.find(
          (data) => data.name == addNewLinesBetweenWords(result.category),
        );
        targetCategory.children.push({
          itemStyle: { color: `${targetCategory.itemStyle.color}` },
          name: addNewLinesBetweenWords(result.name),
          value: result.huntPackageCount,
        });
      }
    }
    return sunburstData;
  }

  public updateSelectedSection(section: 'overview' | 'hunt-package' | 'conclusion' | 'coverage'): void {
    this.selectedSection = section;
    this.cdr.detectChanges();
  }

  onSelectSection(sectionId: string): void {
    this.router.navigate([sectionId], { relativeTo: this.route });
    this.selectedSectionId = sectionId;
  }

}
